import React, { useMemo, useState } from 'react';
import { Chip, Heading, IconButton, TextField } from '@lumx/react';
import MdiIcon from '@mdi/react';
import { mdiArrowLeft, mdiMagnify } from '@mdi/js';
import { User } from '../types';
import { MOCK_USERS } from '../data/mockData';

interface Props {
  nodeId: string;
  nodeName: string;
  onBack: () => void;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const initials = (u: User) => `${u.firstName[0]}${u.lastName[0]}`.toUpperCase();

const AVATAR_COLORS = ['#1546C1', '#7B1FA2', '#00796B', '#E64A19', '#1565C0', '#2E7D32'];
const avatarColor = (u: User) => AVATAR_COLORS[u.id.charCodeAt(u.id.length - 1) % AVATAR_COLORS.length];

const UsersPage: React.FC<Props> = ({ nodeName, onBack }) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return MOCK_USERS;
    return MOCK_USERS.filter(u =>
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)
    );
  }, [search]);

  return (
    <div className="users-page">
      {/* Header */}
      <div className="users-page__header">
        <IconButton
          label="Back"
          icon={mdiArrowLeft}
          emphasis="low"
          onClick={onBack}
        />
        <Heading as="h1" typography="headline">
          Users in {nodeName}
        </Heading>
        <div style={{ flex: 1 }} />
        <TextField
          value={search}
          onChange={setSearch}
          placeholder="Search"
          before={<MdiIcon path={mdiMagnify} size={0.85} />}
          style={{ width: 280 }}
        />
      </div>

      {/* Table */}
      <div className="users-table">
        <div className="users-thead">
          <div />
          <div className="users-th">Name</div>
          <div className="users-th">Status</div>
          <div className="users-th">Account type</div>
          <div className="users-th">Last update</div>
        </div>

        {filtered.map(u => (
          <div key={u.id} className="users-row">
            {/* Avatar */}
            <div className="users-row__avatar">
              {u.avatarUrl ? (
                <img src={u.avatarUrl} alt="" className="users-avatar-img" />
              ) : (
                <span className="users-avatar-initials" style={{ background: avatarColor(u) }}>
                  {initials(u)}
                </span>
              )}
            </div>

            {/* Name + email */}
            <div className="users-row__name">
              <span className="users-row__fullname">{u.firstName} {u.lastName}</span>
              <span className="users-row__email">{u.email}</span>
            </div>

            {/* Status */}
            <div className="users-row__status">
              <Chip
                size="s"
                color={u.status === 'active' ? 'green' : 'grey'}
              >
                {u.status === 'active' ? 'Active' : 'Inactive'}
              </Chip>
            </div>

            {/* Account type */}
            <div className="users-row__account">{u.accountType}</div>

            {/* Last update */}
            <div className="users-row__date">{formatDate(u.updatedAt)}</div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="users-empty">No users match "{search}"</div>
        )}
      </div>
    </div>
  );
};

export default UsersPage;
